import { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://adhvaacaddemy-in-1.onrender.com';

/**
 * Custom hook for fetching and managing landing page sections
 * @param {boolean} visibleOnly - If true, only fetch visible sections (default: true)
 * @returns {Object} - { sections, loading, error, refetch }
 */
export const useSections = (visibleOnly = true) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = visibleOnly 
        ? `${API_BASE_URL}/api/sections/visible`
        : `${API_BASE_URL}/api/sections`;
      
      const response = await axios.get(endpoint);
      setSections(response.data || []);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setError(err.message || 'Failed to fetch sections');
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [visibleOnly]);

  return {
    sections,
    loading,
    error,
    refetch: fetchSections
  };
};

/**
 * Helper function to update a section
 * @param {string} sectionId - Section UUID
 * @param {Object} data - Data to update (name, is_visible, config, etc.)
 * @returns {Promise} - Updated section data
 */
export const updateSection = async (sectionId, data) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/admin/sections/${sectionId}`,
      data
    );
    return response.data;
  } catch (err) {
    console.error('Error updating section:', err);
    throw err;
  }
};

/**
 * Helper function to toggle section visibility
 * @param {string} sectionId - Section UUID
 * @param {boolean} isVisible - New visibility state
 * @returns {Promise} - Updated section data
 */
export const toggleSectionVisibility = async (sectionId, isVisible) => {
  return updateSection(sectionId, { is_visible: isVisible });
};

/**
 * Helper function to reorder sections
 * @param {Array} sections - Array of {id, display_order} objects
 * @returns {Promise} - Updated sections array
 */
export const reorderSections = async (sections) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/admin/sections/reorder`,
      { sections }
    );
    return response.data;
  } catch (err) {
    console.error('Error reordering sections:', err);
    throw err;
  }
};

/**
 * Helper function to create a new section
 * @param {Object} data - Section data (name, type, display_order, is_visible, config)
 * @returns {Promise} - Created section data
 */
export const createSection = async (data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/admin/sections`,
      data
    );
    return response.data;
  } catch (err) {
    console.error('Error creating section:', err);
    throw err;
  }
};

/**
 * Helper function to delete a section
 * @param {string} sectionId - Section UUID
 * @returns {Promise} - Deletion confirmation
 */
export const deleteSection = async (sectionId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/admin/sections/${sectionId}`
    );
    return response.data;
  } catch (err) {
    console.error('Error deleting section:', err);
    throw err;
  }
};

export default useSections;
